// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { auth } from '@/lib/firebase'
import type { OrgOption, TenantResolution } from '@/types'

// query-health-api base URL. Empty by default → same-origin relative paths
// (the Vite dev proxy / prod ingress routes /v1 to the API). Set
// VITE_API_BASE_URL to point at a different API host.
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

// Operator "act-as-org" context. When a QH/Quantino operator selects a client
// org, that org id is sent as X-Org-Context on /v1/* data calls so the backend
// re-scopes RLS to it. null = operator's own org (empty patients, by design) or
// a non-operator (the auth-context setter only sets this for operators). The
// header is is_operator-gated + audited server-side; non-operators sending it
// get 403, so we never set it for them.
let orgContext: string | null = null

/** Set (or clear, with null) the operator act-as-org id for /v1/* calls. */
export function setApiOrgContext(orgId: string | null): void {
  orgContext = orgId
}

/** Current operator act-as-org id, or null. */
export function getApiOrgContext(): string | null {
  return orgContext
}

// Structured API error so callers can branch on status + code (e.g. resolver 400).
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? `API error ${status}: ${code}`)
    this.name = 'ApiError'
  }
}

// Single entry point for query-health-api calls. Resolves the API base URL,
// attaches a fresh X-Request-ID and — when a user is signed in — the Firebase
// ID token as a Bearer credential. getIdToken() returns a cached token and
// transparently refreshes it when expired.
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const url = /^https?:\/\//.test(input) ? input : `${API_BASE}${input}`

  const headers = new Headers(init.headers)
  headers.set('X-Request-ID', crypto.randomUUID())

  const user = auth.currentUser
  if (user) {
    const token = await user.getIdToken()
    headers.set('Authorization', `Bearer ${token}`)
  }

  // Operator act-as: scope /v1/* data calls to the selected org. Excludes the
  // pre-auth /v1/auth/* routes and the control-plane /admin/* (the org list must
  // stay complete so the switcher can show every org).
  if (orgContext && input.startsWith('/v1/') && !input.startsWith('/v1/auth/')) {
    headers.set('X-Org-Context', orgContext)
  }

  // no-store: never serve PHI from the browser HTTP cache, and so background
  // refetch/polls (e.g. post-refresh clinical data) always see fresh server state.
  return fetch(url, { cache: 'no-store', ...init, headers })
}

// Typed request wrapper — handles non-ok responses (surfacing the backend's
// `error` code) and parses JSON. Used by the auth endpoints below; page-level
// fetches that need custom handling call apiFetch directly.
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init)

  if (!res.ok) {
    let code = 'unknown'
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) code = body.error
    } catch {
      // body not JSON — keep 'unknown'
    }
    throw new ApiError(res.status, code, `API ${res.status}: ${code}`)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}

// ── Auth: home-realm discovery + session bootstrap ───────────────────────────

// Pre-auth tenant resolver (public — no Authorization needed). Maps the
// signing-in user's work email (or an org-slug fallback for cross-domain
// admins) to a Firebase tenant. The endpoint returns a uniform 200 even for
// unknown domains — an EMPTY `tenantId` means "no org" (the LoginPage shows the
// fallback / contact-admin path); a malformed email yields ApiError(400). This
// is the signing-in user's own credentialing email, not patient PHI.
export function resolveTenant(email?: string, slug?: string): Promise<TenantResolution> {
  const params = new URLSearchParams()
  if (email) params.set('email', email)
  if (slug) params.set('slug', slug)
  return request<TenantResolution>(`/v1/auth/tenant?${params.toString()}`)
}

// Returned body of POST /v1/auth/bootstrap. The authoritative values are the
// {org_id, role} custom claims read off the refreshed ID token — this body is
// informational. Kept loose to tolerate snake/camel drift across the contract.
export interface BootstrapResult {
  org_id?: string
  role?: string
}

// Binds the signed-in Firebase UID to its membership record and stamps
// {org_id, role} custom claims. Idempotent — safe to call on every first-login
// where claims are absent. Caller must force-refresh the ID token afterward.
export function bootstrapSession(): Promise<BootstrapResult> {
  return request<BootstrapResult>('/v1/auth/bootstrap', { method: 'POST' })
}

// ── Org member lifecycle (org_admin-gated; keyed by stable member id) ────────

// MemberResponse returned by the member-action endpoints.
export interface OrgMember {
  id: string
  email: string
  role: string
  status: string
  uid?: string
}

/** List the acting org's members (invited + active). Strictly org-scoped server-side —
 * matches exactly what resend/change-role/remove can act on (unlike /v1/dashboard, which
 * for operators surfaced cross-org members that then 404'd on remove). */
export function listMembers(): Promise<OrgMember[]> {
  return request<{ members: OrgMember[] }>('/v1/org/members').then((r) => r.members ?? [])
}

/** Re-send the SSO invite email + refresh the sign-in window (pending members). */
export function resendInvite(id: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}/resend`, { method: 'POST' })
}

/** Change a member's role (org_admin | clinic_staff | provider). */
export function changeMemberRole(id: string, role: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  })
}

/** Remove a member — cancels a pending invite or offboards an active member. Idempotent. */
export function removeMember(id: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// ── Operator org list (for the cross-org act-as switcher) ────────────────────

// Orgs an operator may act-as. Operator-gated control-plane list; non-operators
// get 403 and never render the switcher. Validated at the boundary.
export async function getOperatorOrgs(): Promise<OrgOption[]> {
  const body = await request<{ orgs?: unknown }>('/admin/orgs')
  const orgs = Array.isArray(body.orgs) ? body.orgs : []
  return orgs
    .filter(
      (o): o is OrgOption =>
        !!o &&
        typeof o === 'object' &&
        typeof (o as { id?: unknown }).id === 'string' &&
        typeof (o as { slug?: unknown }).slug === 'string' &&
        typeof (o as { displayName?: unknown }).displayName === 'string',
    )
    .map(({ id, slug, displayName }) => ({ id, slug, displayName }))
}
